import React from 'react';

interface PageHeroProps {
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  eyebrow?: string;
  children?: React.ReactNode;
  ariaLabel?: string;
  id?: string;
  /** Custom max-width for the content container, defaults to max-w-4xl */
  contentClassName?: string;
}

/**
 * Standardized Hero section for interior pages (Portfolio, Services, About, Contact, etc.)
 * Confines text to a readable max-width on the left.
 */
export const PageHero = ({ 
  title, 
  subtitle, 
  eyebrow, 
  children, 
  ariaLabel,
  id,
  contentClassName = "max-w-4xl"
}: PageHeroProps) => {
  return (
    <section 
      className="bg-bloom-green-deep min-h-[40vh] pt-48 pb-24 flex items-center relative overflow-hidden"
      aria-label={ariaLabel || "Introduction"}
    >
      <div className="container mx-auto px-6 relative z-10">
        <div className={contentClassName}>
          {eyebrow && (
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-[2px] bg-bloom-gold" />
              <span className="font-sans text-xs font-bold uppercase tracking-widest text-bloom-gold">
                {eyebrow}
              </span>
            </div>
          )}
          <h1 id={id} className="heading-hero text-bloom-cream mb-8">
            {title}
          </h1>
          {subtitle && (
            <div className="font-sans text-lg md:text-xl text-bloom-cream/90 leading-relaxed max-w-2xl">
              {subtitle}
            </div>
          )}
          {children && (
            <div className="mt-10">
              {children}
            </div>
          )}
        </div>
      </div>
      {/* Subtle decorative grid */}
      <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-5 pointer-events-none" />
    </section>
  );
};
